import React, { createContext, useCallback, useContext, useState } from 'react';

export type ScoringEngine = 'manual' | 'fuse';

interface DebugState {
  enabled: boolean;
  engine: ScoringEngine;
  showScores: boolean;
  fuseThreshold: number;
  fuseIgnoreLocation: boolean;
}

const DEFAULTS: DebugState = {
  enabled: false,
  engine: 'fuse',
  showScores: true,
  fuseThreshold: 0.45,
  fuseIgnoreLocation: true,
};

const LS_KEY = 'cp:scoring_debug';

function load(): DebugState {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (raw) return { ...DEFAULTS, ...JSON.parse(raw) };
  } catch { /* ignore */ }
  return DEFAULTS;
}

function save(s: DebugState): void {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(s));
  } catch { /* ignore */ }
}

interface ScoringDebugContextValue extends DebugState {
  toggle: () => void;
  set: <K extends keyof DebugState>(key: K, value: DebugState[K]) => void;
}

const ScoringDebugContext = createContext<ScoringDebugContextValue | null>(null);

export const ScoringDebugProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [state, setState] = useState<DebugState>(load);

  const update = useCallback((patch: Partial<DebugState>) => {
    setState(prev => {
      const next = { ...prev, ...patch };
      save(next);
      return next;
    });
  }, []);

  const toggle = useCallback(() => {
    setState(prev => {
      const next = { ...prev, enabled: !prev.enabled };
      save(next);
      return next;
    });
  }, []);

  const set = useCallback(<K extends keyof DebugState>(key: K, value: DebugState[K]) => {
    update({ [key]: value } as Partial<DebugState>);
  }, [update]);

  return (
    <ScoringDebugContext.Provider value={{ ...state, toggle, set }}>
      {children}
    </ScoringDebugContext.Provider>
  );
};

export function useScoringDebug(): ScoringDebugContextValue {
  const ctx = useContext(ScoringDebugContext);
  if (!ctx) throw new Error('useScoringDebug must be inside ScoringDebugProvider');
  return ctx;
}
